// src/components/AuthPage.js
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import "../../../src/globalCss.css";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { setUpPassword, verifySetUp } from "../../apis/apisList/userApi";
import { useApp } from "../../Context/AppContext";

const auth_box = {
  loginCol: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};

const CompleteProfilePage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({
    password: "",
    confirmPassword: "",
    otp: "",
  });
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfPassword, setShowConfPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const redirectPage = searchParams.get("redirect");
  const email = searchParams.get("email");
  const { token } = useParams();
  const { login } = useApp();
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!password) {
      newErrors.password = "Password is required.";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters.";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required.";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const res = await setUpPassword(token, {
        password,
        confirmPassword,
      });
      await login(res);
      setSuccessMessage("Account setup completed. Redirecting to dashboard...");
      const redirect = redirectPage
        ? redirectPage.startsWith("/")
          ? redirectPage
          : `/${redirectPage}`
        : "/dashboard";
      console.log(">>>redirect>>", redirect);
      setTimeout(() => navigate(redirect), 3000);
    } catch (error) {
      if (error?.status === "401") {
        navigate("/login", { replace: true });
      } else if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        const message =
          error?.response?.data?.message || "Something went wrong. Try again.";
        console.error("Error updating password:", error);
        setErrors({ otp: message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkSetup = async () => {
      setLoading(true);
      try {
        const jwtToken = localStorage.getItem("jwtToken");
        if (jwtToken) {
          navigate(redirectPage);
        } else {
          const res = await verifySetUp(token);
          if (res?.user) {
            if (!res?.user.pendingPasswordSetup) {
              navigate(`/login?redirect=${redirectPage}`);
            } else {
              setLoading(false);
            }
          } else {
            setLoading(false);
          }
        }
      } catch (err) {
        if (err?.response?.data?.message) {
          navigate("/login", { replace: true });
        }
      } finally {
        setLoading(false);
      }
    };

    checkSetup();
  }, [redirectPage]);

  if (loading) {
    return <>Loading</>;
  }

  return (
    <Container maxWidth="xl">
      <Box className="dashboard_frame">
        <Box className="backBtnFrame">
          <Button
            className="backBtn"
            variant="contained"
            sx={{ marginBottom: 2 }}
            onClick={() => (window.location.href = "/")}
          >
            <ArrowBackIcon sx={{ marginRight: 1 }} />
            Back to Homepage
          </Button>
        </Box>

        <Box
          style={auth_box.logRegBox}
          sx={{
            display: { xs: "block", sm: "block", md: "flex" },
            padding: {
              xs: "30px 30px 30px 30px",
              sm: "30px 30px 30px 30px",
              md: "60px 60px 30px 60px",
            },
          }}
        >
          <Box
            style={auth_box.loginCol}
            sx={{ width: { xs: "100%", sm: "100%", md: "100%" } }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "355px",
                margin: "0 auto",
                padding: "100px 15px",
              }}
            >
              <Typography
                variant="h6"
                align="center"
                sx={{ marginBottom: 4, paddingBottom: 2, borderBottom: 2 }}
              >
                Setup account password to access your project
              </Typography>

              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                value={email || ""}
                sx={{ marginBottom: 3 }}
                disabled={true}
              />
              <TextField
                label="New Password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!errors.password}
                helperText={errors.password}
                sx={{ marginBottom: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Confirm Password"
                type={showConfPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                sx={{ marginBottom: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfPassword(!showConfPassword)}
                      >
                        {showConfPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {successMessage && (
                <Typography
                  variant="body1"
                  align="center"
                  sx={{ color: "green" }}
                >
                  {successMessage}
                </Typography>
              )}

              <Button
                variant="contained"
                fullWidth
                onClick={handleSubmit}
                sx={{ marginBottom: 2 }}
                style={{
                  backgroundColor: "#065F9C",
                  boxShadow: "none",
                  borderRadius: "10px",
                  padding: "12px 20px",
                  fontSize: "20px",
                  fontWeight: "500",
                  textTransform: "inherit",
                }}
                disabled={isLoading}
              >
                {isLoading ? "Submitting..." : "Complete Profile"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default CompleteProfilePage;
